import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon } from "lucide-react";

// Import TTS provider configuration
import ttsProviders from "@/config/tts-providers.json";
import {
  isRealtimeModel,
  getRealtimeModelMessage,
  isGeminiRealtimeModel,
  OPENAI_REALTIME_VOICES,
} from "@/utils/realtimeModels";

// Import Smallest AI voice data
import lightningLargeVoices from "../models/tts/smallest_ai/lightning-large.json";
import lightningV2Voices from "../models/tts/smallest_ai/lightning-v2.json";
import lightningVoices from "../models/tts/smallest_ai/lightning.json";

function ElevenLabsFields({ control, setValue }) {
  const tts_model = control._formValues.tts_model;
  const provider = ttsProviders.providers.elevenlabs;

  const getElevenlabsModels = () => {
    if (!provider?.models) return [];

    const allModels = [];
    Object.entries(provider.models).forEach(([categoryName, modelCategory]) => {
      if (Array.isArray(modelCategory)) {
        modelCategory.forEach((model) => {
          allModels.push({
            ...model,
            category: categoryName,
          });
        });
      }
    });

    return allModels;
  };

  const getElevenlabsVoices = () => {
    if (!provider?.voices?.professional) return [];

    return provider.voices.professional.map((voice) => ({
      voice_id: voice.id,
      name: voice.name,
      gender: voice.gender,
      accent: voice.accent,
    }));
  };

  return (
    <>
      <FormField
        control={control}
        name="tts_model"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">TTS Model</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select TTS model" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getElevenlabsModels().map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name}
                    {model.isDefault && " (Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Choose the TTS model that best fits your needs
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="flex justify-between gap-4">
        <FormField
          control={control}
          name="voice"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel className="text-foreground">Voice</FormLabel>
              <Select
                onValueChange={(value) => {
                  if (value === "custom") {
                    field.onChange("");
                  } else {
                    field.onChange(value);
                  }
                }}
                value={
                  field.value &&
                  !getElevenlabsVoices().find((v) => v.voice_id === field.value)
                    ? "custom"
                    : field.value
                }
              >
                <FormControl>
                  <SelectTrigger className="bg-muted text-foreground border-input">
                    <SelectValue placeholder="Select a voice" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {getElevenlabsVoices().map((voice) => (
                    <SelectItem key={voice.voice_id} value={voice.voice_id}>
                      {voice.name} ({voice.gender}, {voice.accent})
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom Voice ID</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription className="text-muted-foreground">
                Select from available ElevenLabs voices or use a custom voice ID
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {(control._formValues.voice === "" ||
        (control._formValues.voice &&
          !getElevenlabsVoices().find(
            (v) => v.voice_id === control._formValues.voice
          ))) && (
        <FormField
          control={control}
          name="voice"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">Custom Voice ID</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter your custom voice ID"
                  className="bg-muted text-foreground border-input"
                  {...field}
                />
              </FormControl>
              <FormDescription className="text-muted-foreground">
                Enter a custom ElevenLabs voice ID
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* ElevenLabs Voice Tuning Fields - Always Rendered */}
      <div className="mt-6 space-y-6">
        <h4 className="text-md font-medium text-foreground">Voice Tuning</h4>
        
        <FormField
          control={control}
          name="similarity_boost"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between mb-2">
                <FormLabel className="text-foreground">
                  Similarity Boost
                </FormLabel>
                <div className="text-foreground">
                  {field.value ?? 0.5}
                </div>
              </div>
              <FormDescription className="text-muted-foreground mb-2">
                Boost the similarity of the synthesized speech to the original
                voice.
              </FormDescription>
              <FormControl>
                <Slider
                  min={0}
                  max={1}
                  step={0.1}
                  defaultValue={[field.value ?? 0.5]}
                  onValueChange={(vals) => field.onChange(vals[0])}
                  className="text-neon-purple"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="style"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between mb-2">
                <FormLabel className="text-foreground">Style</FormLabel>
                <div className="text-foreground">
                  {field.value ?? 0}
                </div>
              </div>
              <FormDescription className="text-muted-foreground mb-2">
                Amplify the style of the original speaker. Use higher values
                for more expressive speech.
              </FormDescription>
              <FormControl>
                <Slider
                  min={0}
                  max={1}
                  step={0.1}
                  defaultValue={[field.value ?? 0]}
                  onValueChange={(vals) => field.onChange(vals[0])}
                  className="text-neon-purple"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="stability"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between mb-2">
                <FormLabel className="text-foreground">Stability</FormLabel>
                <div className="text-foreground">
                  {field.value ?? 0.5}
                </div>
              </div>
              <FormDescription className="text-muted-foreground mb-2">
                Higher stability makes the voice more consistent but less
                expressive.
              </FormDescription>
              <FormControl>
                <Slider
                  min={0}
                  max={1}
                  step={0.1}
                  defaultValue={[field.value ?? 0.5]}
                  onValueChange={(vals) => field.onChange(vals[0])}
                  className="text-neon-purple"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="speed"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between mb-2">
                <FormLabel className="text-foreground">Speed</FormLabel>
                <div className="text-foreground">
                  {field.value ?? 1}x
                </div>
              </div>
              <FormDescription className="text-muted-foreground mb-2">
                Adjust the speed of speech generation. Higher values make
                speech faster.
              </FormDescription>
              <FormControl>
                <Slider
                  min={0.25}
                  max={4}
                  step={0.1}
                  defaultValue={[field.value ?? 1]}
                  onValueChange={(vals) => field.onChange(vals[0])}
                  className="text-neon-purple"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="useSpeakerBoost"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base text-foreground">
                  Use Speaker Boost
                </FormLabel>
                <FormDescription className="text-muted-foreground">
                  Boost the similarity of the synthesized speech and the voice
                  at the cost of some generation speed.
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
}

function SmallestAIFields({ control, setValue }) {
  const tts_model = control._formValues.tts_model;
  const provider = ttsProviders.providers.smallestai;

  const getSmallestAIModels = () => {
    if (!provider?.models) return [];

    const allModels = [];
    Object.entries(provider.models).forEach(([categoryName, modelCategory]) => {
      if (Array.isArray(modelCategory)) {
        modelCategory.forEach((model) => {
          allModels.push({
            ...model,
            category: categoryName,
          });
        });
      }
    });

    return allModels;
  };

  const getSmallestAIVoices = () => {
    if (tts_model === "lightning") {
      return lightningVoices.voices;
    } else if (tts_model === "lightning-v2") {
      return lightningV2Voices.voices;
    } else if (tts_model === "lightning-large") {
      return lightningLargeVoices.voices;
    }
    return [];
  };

  return (
    <>
      <FormField
        control={control}
        name="tts_model"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">TTS Model</FormLabel>
            <Select
              onValueChange={(value) => {
                field.onChange(value);
                setValue("voice", "");
              }}
              value={field.value}
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select TTS model" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getSmallestAIModels().map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name}
                    {model.beta && " (Beta)"}
                    {model.deprecated && " (Deprecated)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              {
                getSmallestAIModels().find((m) => m.id === field.value)
                  ?.description
              }
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="voice"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Voice</FormLabel>
            <Select
              onValueChange={(value) => {
                if (value === "custom") {
                  field.onChange("");
                } else {
                  field.onChange(value);
                }
              }}
              value={
                field.value &&
                !getSmallestAIVoices().find((v) => v.voiceId === field.value)
                  ? "custom"
                  : field.value || ""
              }
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select a voice" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getSmallestAIVoices().map((voice) => (
                  <SelectItem key={voice.voiceId} value={voice.voiceId}>
                    {voice.displayName} ({voice.tags.gender},{" "}
                    {voice.tags.accent})
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom Voice ID</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select a voice from the available options or use a custom voice ID
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      {(control._formValues.voice === "" ||
        (control._formValues.voice &&
          !getSmallestAIVoices().find(
            (v) => v.voiceId === control._formValues.voice
          ))) && (
        <FormField
          control={control}
          name="voice"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">Custom Voice ID</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter your custom voice ID"
                  className="bg-muted text-foreground border-input"
                  {...field}
                />
              </FormControl>
              <FormDescription className="text-muted-foreground">
                Enter a custom Smallest AI voice ID
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </>
  );
}

function GroqTTSFields({ control, setValue }) {
  const provider = ttsProviders.providers.groq;

  const getGroqModels = () => {
    if (!provider?.models) return [];

    const allModels = [];
    Object.entries(provider.models).forEach(([categoryName, modelCategory]) => {
      if (Array.isArray(modelCategory)) {
        modelCategory.forEach((model) => {
          allModels.push({
            ...model,
            category: categoryName,
          });
        });
      }
    });

    return allModels;
  };

  return (
    <>
      <FormField
        control={control}
        name="tts_model"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">TTS Model</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select Groq TTS model" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getGroqModels().map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name}
                    {model.isDefault && " (Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select a Groq TTS model
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="voice"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">
              Voice ID (Optional)
            </FormLabel>
            <FormControl>
              <Input
                placeholder="Voice ID (leave blank for default voice)"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormDescription className="text-muted-foreground">
              Optional voice ID - leave blank to use the default voice
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="apiKey"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">Groq API Key</FormLabel>
            <FormControl>
              <Input
                type="password"
                placeholder="Your Groq API key"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormDescription className="text-muted-foreground">
              Required for Groq services. Get it from console.groq.com
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

function CartesiaFields({ control, setValue }) {
  const provider = ttsProviders.providers.cartesia;
  const tts_model = control._formValues.tts_model;
  
  // Initialize default values when component mounts or provider changes
  useEffect(() => {
    // Set default model if not already set
    if (!tts_model || !getCartesiaModels().find(m => m.id === tts_model)) {
      if (provider?.defaultModel) {
        setValue("tts_model", provider.defaultModel, { shouldValidate: true });
      }
    }
    
    // Set default language if not set
    if (!control._formValues.tts_language) {
      const languages = getCartesiaLanguages();
      if (languages.length > 0) {
        setValue("tts_language", languages[0].id, { shouldValidate: true });
      }
    }
    
    // Set default speed if not set
    const currentSpeed = control._formValues.speed;
    const isSonic3 = tts_model === 'sonic-3';

    if (!currentSpeed) {
      if (isSonic3) {
        // Sonic 3: Use numeric default speed (1.0)
        setValue("speed", 1.0, { shouldValidate: true });
      } else {
        // Other models: Use string-based speed presets
        setValue("speed", "normal", { shouldValidate: true });
      }
    } else if (isSonic3 && typeof currentSpeed === 'string') {
      // Convert string-based speeds to numeric for Sonic 3
      const speedMap = { 'slow': 0.8, 'normal': 1.0, 'fast': 1.3 };
      const numericSpeed = speedMap[currentSpeed] || 1.0;
      setValue("speed", numericSpeed, { shouldValidate: true });
    } else if (!isSonic3 && typeof currentSpeed === 'number') {
      // Convert numeric speeds to string presets for non-Sonic 3 models
      let stringSpeed = 'normal';
      if (currentSpeed <= 0.9) stringSpeed = 'slow';
      else if (currentSpeed >= 1.1) stringSpeed = 'fast';
      setValue("speed", stringSpeed, { shouldValidate: true });
    }
    
    // Set default voice if not set
    if (!control._formValues.voice) {
      const voices = getCartesiaVoices();
      if (voices.length > 0) {
        setValue("voice", voices[0].id, { shouldValidate: true });
      }
    }
  }, [control._formValues.tts_provider]);
  
  const getCartesiaModels = () => {
    return (provider?.models || []).map(model => 
      typeof model === 'string' 
        ? { id: model, name: model, description: '', isDefault: false } 
        : { ...model, description: model.description || '', isDefault: model.isDefault || false }
    );
  };

  const getCartesiaLanguages = () => {
    return provider?.languages || [];
  };

  const getCartesiaSpeeds = () => {
    return provider?.speeds || [];
  };

  const getCartesiaEmotions = () => {
    return provider?.emotions || [];
  };

  const getCartesiaVoices = () => {
    return provider?.voices || [];
  };

  return (
    <>
      <FormField
        control={control}
        name="tts_model"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">TTS Model</FormLabel>
            <Select
              onValueChange={(value) => {
                field.onChange(value);
              }}
              value={field.value || provider?.defaultModel}
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select TTS model" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getCartesiaModels().map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name}
                    {model.isDefault && " (Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              {getCartesiaModels().find(m => m.id === field.value)?.description || 
               getCartesiaModels().find(m => m.id === provider?.defaultModel)?.description}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Voice</FormLabel>
            <Select 
              onValueChange={(value) => {
                if (value === "custom") {
                  field.onChange("");
                } else {
                  field.onChange(value);
                }
              }} 
              value={field.value && !getCartesiaVoices().find(v => v.id === field.value) ? "custom" : field.value}
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select a voice" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getCartesiaVoices().map((voice) => (
                  <SelectItem key={voice.id} value={voice.id}>
                    {voice.name} ({voice.gender}, {voice.style})
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom Voice ID</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select from available Cartesia voices or use a custom voice ID
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {(control._formValues.voice === "" || (control._formValues.voice && !getCartesiaVoices().find(v => v.id === control._formValues.voice))) && (
        <FormField
          control={control}
          name="voice"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">Custom Voice ID</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Enter your custom voice ID"
                  className="bg-muted text-foreground border-input"
                  {...field}
                />
              </FormControl>
              <FormDescription className="text-muted-foreground">
                Enter a custom Cartesia voice ID
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={control}
        name="tts_language"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Language</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value}
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getCartesiaLanguages().map((language) => (
                  <SelectItem key={language.id} value={language.id}>
                    {language.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              {field.value === "multilingual"
                ? "The voice speaks whatever language each sentence is written in — prompt the agent to mix only the languages you want (e.g. Tamil, Malayalam, Kannada, English)."
                : "Select the language for speech synthesis"}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="speed"
        render={({ field }) => {
          // Check if current model is Sonic 3
          const isSonic3 = tts_model === 'sonic-3';

          if (isSonic3) {
            // Sonic 3: Use slider with numeric values (0.6 to 2.0)
            let speedValue = field.value;
            if (typeof speedValue === 'string') {
              // Convert old string values to numeric
              const speedMap = { 'slow': 0.8, 'normal': 1.0, 'fast': 1.3 };
              speedValue = speedMap[speedValue] || 1.0;
            }
            speedValue = speedValue || 1.0;

            // Clamp to valid range
            speedValue = Math.max(0.6, Math.min(2.0, speedValue));

            return (
              <FormItem>
                <div className="flex items-center justify-between mb-2">
                  <FormLabel className="text-foreground">
                    Speed
                  </FormLabel>
                  <div className="text-foreground">
                    {speedValue.toFixed(2)}x
                  </div>
                </div>
                <FormDescription className="text-muted-foreground mb-2">
                  Speech speed (0.6 to 2.0). Sonic 3 supports precise speed control.
                </FormDescription>
                <FormControl>
                  <Slider
                    min={0.6}
                    max={2.0}
                    step={0.1}
                    value={[speedValue]}
                    onValueChange={(vals) => field.onChange(vals[0])}
                    className="text-neon-purple"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          } else {
            // Sonic 2 and others: Use dropdown with presets
            let speedValue = field.value;
            if (typeof speedValue === 'number') {
              // Map numeric values to string presets
              if (speedValue <= 0.9) speedValue = 'slow';
              else if (speedValue >= 1.1) speedValue = 'fast';
              else speedValue = 'normal';
            }

            return (
              <FormItem className="flex-1">
                <FormLabel className="text-foreground">Speed</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={speedValue || 'normal'}
                >
                  <FormControl>
                    <SelectTrigger className="bg-muted text-foreground border-input">
                      <SelectValue placeholder="Select speed" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {getCartesiaSpeeds().map((speed) => (
                      <SelectItem key={speed.id} value={speed.id}>
                        {speed.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription className="text-muted-foreground">
                  Select the speech rate
                </FormDescription>
                <FormMessage />
              </FormItem>
            );
          }
        }}
      />

      <FormField
        control={control}
        name="emotion"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Emotion (Optional)</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value || "none"}
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select emotion (optional)" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {getCartesiaEmotions().map((emotion) => (
                  <SelectItem key={emotion.id} value={emotion.id}>
                    {emotion.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Optional emotional tone for the voice
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* API key removed - will be passed from .env file */}
    </>
  );
}

function SarvamTTSFields({ control, setValue }) {
  const provider = ttsProviders.providers.sarvam;

  // Helper function to get display name for language codes
  const getLanguageDisplayName = (langCode) => {
    const languageNames = {
      'en': 'English',
      'hi': 'Hindi',
      'ta': 'Tamil', 
      'te': 'Telugu',
      'ml': 'Malayalam',
      'kn': 'Kannada',
      'gu': 'Gujarati',
      'mr': 'Marathi',
      'bn': 'Bengali'
    };
    return languageNames[langCode] || langCode.toUpperCase();
  };

  const getSarvamModels = () => {
    if (!provider?.models) return [];
    // Sarvam models are stored as an array directly
    return provider.models.map(model => ({
      id: model.id,
      name: model.name,
      description: model.description || 'Indic language text-to-speech',
      isDefault: model.isDefault || false
    }));
  };

  const getSarvamVoices = () => {
    return provider?.voices || [];
  };

  const getSarvamLanguages = () => {
    if (!provider?.settings?.language?.enum) return [];
    return provider.settings.language.enum.map(lang => ({
      value: lang,
      label: getLanguageDisplayName(lang)
    }));
  };

  return (
    <>
      <FormField
        control={control}
        name="tts_model"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">TTS Model</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select TTS model" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getSarvamModels().map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name}
                    {model.isDefault && " (Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              {getSarvamModels().find(m => m.id === field.value)?.description || 
               "Indic language text-to-speech model"}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="language"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Target Language</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value || provider?.settings?.language?.default}
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select target language" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getSarvamLanguages().map((language) => (
                  <SelectItem key={language.value} value={language.value}>
                    {language.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select the target language for speech synthesis (this will determine the language of the generated speech)
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Voice</FormLabel>
            <Select
              onValueChange={(value) => {
                if (value === "custom") {
                  field.onChange("");
                } else {
                  field.onChange(value);
                }
              }}
              value={
                field.value && !getSarvamVoices().find(v => v.id === field.value)
                  ? "custom"
                  : field.value || ""
              }
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select a voice" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getSarvamVoices().map((voice) => (
                  <SelectItem key={voice.id} value={voice.id}>
                    {voice.name} ({voice.gender}, {voice.accent})
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom Voice ID</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select from available Sarvam voices or use a custom voice ID
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {(control._formValues.voice === "" ||
        (control._formValues.voice &&
          !getSarvamVoices().find(
            (v) => v.id === control._formValues.voice
          ))) && (
        <FormField
          control={control}
          name="voice"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">Custom Voice ID</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter your custom voice ID"
                  className="bg-muted text-foreground border-input"
                  {...field}
                />
              </FormControl>
              <FormDescription className="text-muted-foreground">
                Enter a custom Sarvam voice ID
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={control}
        name="speed"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Speed</FormLabel>
              <div className="text-foreground">
                {field.value || provider?.settings?.speed?.default}x
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust the speed of speech generation. Higher values make speech faster.
            </FormDescription>
            <FormControl>
              <Slider
                min={provider?.settings?.speed?.min || 0.5}
                max={provider?.settings?.speed?.max || 2.0}
                step={0.1}
                defaultValue={[field.value || provider?.settings?.speed?.default || 1.0]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="pitch"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Pitch</FormLabel>
              <div className="text-foreground">
                {field.value !== undefined ? field.value : provider?.settings?.pitch?.default}
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust the pitch of speech generation. Lower values make speech deeper.
            </FormDescription>
            <FormControl>
              <Slider
                min={provider?.settings?.pitch?.min || -1.0}
                max={provider?.settings?.pitch?.max || 1.0}
                step={0.1}
                defaultValue={[field.value !== undefined ? field.value : provider?.settings?.pitch?.default || -0.2]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="loudness"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Loudness</FormLabel>
              <div className="text-foreground">
                {field.value || provider?.settings?.loudness?.default}x
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust the loudness of speech generation. Higher values make speech louder.
            </FormDescription>
            <FormControl>
              <Slider
                min={provider?.settings?.loudness?.min || 0.5}
                max={provider?.settings?.loudness?.max || 2.0}
                step={0.1}
                defaultValue={[field.value || provider?.settings?.loudness?.default || 1.0]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

    </>
  );
}

function MurfFields({ control, setValue }) {
  const provider = ttsProviders.providers.murf;

  const getMurfModels = () => {
    if (!provider?.models) return [];
    return provider.models.map(model => ({
      id: model.id,
      name: model.name,
      description: model.description || '',
      isDefault: model.isDefault || false
    }));
  };

  const getMurfVoices = () => {
    return provider?.voices || [];
  };

  const getMurfStyles = () => {
    return provider?.styles || [];
  };

  const getMurfLanguages = () => {
    return provider?.languages || [];
  };

  return (
    <>
      <FormField
        control={control}
        name="tts_model"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">TTS Model</FormLabel>
            <Select onValueChange={field.onChange} value={field.value || "FALCON"}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select TTS model" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getMurfModels().map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name}
                    {model.isDefault && " (Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              {getMurfModels().find(m => m.id === field.value)?.description ||
               "Ultra-fast Murf AI TTS model"}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Voice</FormLabel>
            <Select
              onValueChange={(value) => {
                if (value === "custom") {
                  field.onChange("");
                } else {
                  field.onChange(value);
                }
              }}
              value={
                field.value && !getMurfVoices().find(v => v.id === field.value)
                  ? "custom"
                  : field.value || "Matthew"
              }
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select a voice" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getMurfVoices().map((voice) => (
                  <SelectItem key={voice.id} value={voice.id}>
                    {voice.name} ({voice.gender}, {voice.accent})
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom Voice ID</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select from 150+ Murf AI voices across 35+ languages
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {(control._formValues.voice === "" ||
        (control._formValues.voice &&
          !getMurfVoices().find(
            (v) => v.id === control._formValues.voice
          ))) && (
        <FormField
          control={control}
          name="voice"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">Custom Voice ID</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter your custom voice ID"
                  className="bg-muted text-foreground border-input"
                  {...field}
                />
              </FormControl>
              <FormDescription className="text-muted-foreground">
                Enter a custom Murf voice ID from your account
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={control}
        name="style"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Voice Style</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value || "Conversation"}
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select voice style" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getMurfStyles().map((style) => (
                  <SelectItem key={style.id} value={style.id}>
                    {style.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Choose the speaking style for the selected voice
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="language"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Language</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value || "en-US"}
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getMurfLanguages().map((language) => (
                  <SelectItem key={language.id} value={language.id}>
                    {language.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select the language for speech synthesis
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="speed"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Speed</FormLabel>
              <div className="text-foreground">
                {field.value !== undefined ? field.value : 0}
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust speech speed (-50 to 50, where 0 is normal speed)
            </FormDescription>
            <FormControl>
              <Slider
                min={-50}
                max={50}
                step={1}
                defaultValue={[field.value !== undefined ? field.value : 0]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="pitch"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Pitch</FormLabel>
              <div className="text-foreground">
                {field.value !== undefined ? field.value : 0}
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust voice pitch (-50 to 50, where 0 is normal pitch)
            </FormDescription>
            <FormControl>
              <Slider
                min={-50}
                max={50}
                step={1}
                defaultValue={[field.value !== undefined ? field.value : 0]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="sampleRate"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Sample Rate</FormLabel>
            <Select
              onValueChange={(value) => field.onChange(parseInt(value))}
              value={field.value?.toString() || "24000"}
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select sample rate" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="8000">8000 Hz</SelectItem>
                <SelectItem value="24000">24000 Hz (Default)</SelectItem>
                <SelectItem value="44100">44100 Hz</SelectItem>
                <SelectItem value="48000">48000 Hz</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Higher sample rates provide better audio quality
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

function DeepgramTTSFields({ control, setValue }) {
  const provider = ttsProviders.providers.deepgram;

  const getDeepgramModels = () => {
    if (!provider?.models?.tts) return [];

    return provider.models.tts.map((model) => ({
      id: model.id,
      name: model.name,
      type: model.type,
      language: model.language,
      gender: model.gender,
    }));
  };

  const getDeepgramVoices = () => {
    // For Deepgram, models are voices
    return getDeepgramModels();
  };

  return (
    <>
      <FormField
        control={control}
        name="voice"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Voice</FormLabel>
            <Select
              onValueChange={(value) => {
                if (value === "custom") {
                  field.onChange("");
                } else {
                  field.onChange(value);
                }
              }}
              value={
                field.value &&
                !getDeepgramVoices().find((v) => v.id === field.value)
                  ? "custom"
                  : field.value || ""
              }
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select a voice" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getDeepgramVoices().map((voice) => (
                  <SelectItem key={voice.id} value={voice.id}>
                    {voice.name}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom Voice ID</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select a Deepgram voice or use a custom voice ID
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      {(control._formValues.voice === "" ||
        (control._formValues.voice &&
          !getDeepgramVoices().find(
            (v) => v.id === control._formValues.voice
          ))) && (
        <FormField
          control={control}
          name="voice"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">Custom Voice ID</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter your custom voice ID"
                  className="bg-muted text-foreground border-input"
                  {...field}
                />
              </FormControl>
              <FormDescription className="text-muted-foreground">
                Enter a custom Deepgram voice ID
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </>
  );
}

function ResembleFields({ control, setValue }) {
  const provider = ttsProviders.providers.resemble;

  const getResembleModels = () => {
    if (!provider?.models) return [];

    const allModels = [];
    if (Array.isArray(provider.models)) {
      allModels.push(...provider.models);
    } else {
      Object.entries(provider.models).forEach(([categoryName, modelCategory]) => {
        if (Array.isArray(modelCategory)) {
          allModels.push(...modelCategory);
        }
      });
    }

    return allModels;
  };

  const getResembleVoices = () => {
    return provider?.voices || [];
  };

  return (
    <>
      <FormField
        control={control}
        name="tts_model"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">TTS Model</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select TTS model" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getResembleModels().map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name}
                    {model.isDefault && " (Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              {getResembleModels().find(m => m.id === field.value)?.description || 
               "Custom voice cloning model"}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Voice</FormLabel>
            <Select
              onValueChange={(value) => {
                if (value === "custom") {
                  field.onChange("");
                } else {
                  field.onChange(value);
                }
              }}
              value={
                field.value && !getResembleVoices().find(v => v.id === field.value)
                  ? "custom"
                  : field.value || ""
              }
            >
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select a voice" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getResembleVoices().map((voice) => (
                  <SelectItem key={voice.id} value={voice.id}>
                    {voice.name} ({voice.gender}, {voice.accent})
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom Voice UUID</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select from available Resemble voices or use a custom voice UUID
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {(control._formValues.voice === "" ||
        (control._formValues.voice &&
          !getResembleVoices().find(
            (v) => v.id === control._formValues.voice
          ))) && (
        <FormField
          control={control}
          name="voice"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">Custom Voice UUID</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter your custom voice UUID"
                  className="bg-muted text-foreground border-input"
                  {...field}
                />
              </FormControl>
              <FormDescription className="text-muted-foreground">
                Enter a custom Resemble AI voice UUID for cloning
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={control}
        name="voice_uuid"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">Voice UUID for Cloning</FormLabel>
            <FormControl>
              <Input
                placeholder="Voice UUID for voice cloning"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormDescription className="text-muted-foreground">
              UUID of the voice to use for cloning (required for Resemble AI)
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="speed"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Speed</FormLabel>
              <div className="text-foreground">
                {field.value || provider?.settings?.speed?.default || 1.0}x
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust the speed of speech generation. Higher values make speech faster.
            </FormDescription>
            <FormControl>
              <Slider
                min={provider?.settings?.speed?.min || 0.5}
                max={provider?.settings?.speed?.max || 2.0}
                step={0.1}
                defaultValue={[field.value || provider?.settings?.speed?.default || 1.0]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="apiKey"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">Resemble AI API Key</FormLabel>
            <FormControl>
              <Input
                type="password"
                placeholder="Your Resemble AI API key"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormDescription className="text-muted-foreground">
              Required for Resemble AI services. Get it from your Resemble console
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

function OpenAIFields({ control, setValue }) {
  const provider = ttsProviders.providers.openai;

  const getOpenAIModels = () => {
    return provider?.models || [];
  };

  const getOpenAIVoices = () => {
    return provider?.voices || [];
  };

  return (
    <>
      <FormField
        control={control}
        name="tts_model"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">TTS Model</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select TTS model" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getOpenAIModels().map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name}
                    {model.isDefault && " (Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              {getOpenAIModels().find(m => m.id === field.value)?.description || 
               "OpenAI text-to-speech model"}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Voice</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select a voice" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getOpenAIVoices().map((voice) => (
                  <SelectItem key={voice.id} value={voice.id}>
                    {voice.name} ({voice.gender}, {voice.accent})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select from available OpenAI voices
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="speed"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Speed</FormLabel>
              <div className="text-foreground">
                {field.value || provider?.settings?.speed?.default || 1.0}x
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust the speed of speech generation. Higher values make speech faster.
            </FormDescription>
            <FormControl>
              <Slider
                min={provider?.settings?.speed?.min || 0.25}
                max={provider?.settings?.speed?.max || 4.0}
                step={0.1}
                defaultValue={[field.value || provider?.settings?.speed?.default || 1.0]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="apiKey"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">OpenAI API Key</FormLabel>
            <FormControl>
              <Input
                type="password"
                placeholder="Your OpenAI API key"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormDescription className="text-muted-foreground">
              Required for OpenAI TTS services
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

function GoogleTTSFields({ control, setValue }) {
  const provider = ttsProviders.providers.google;

  const getGoogleModels = () => {
    return provider?.models || [];
  };

  const getGoogleVoices = () => {
    return provider?.voices || [];
  };

  return (
    <>
      <FormField
        control={control}
        name="tts_model"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">TTS Model</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select TTS model" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getGoogleModels().map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name}
                    {model.isDefault && " (Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              {getGoogleModels().find(m => m.id === field.value)?.description || 
               "Google Cloud TTS model"}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Voice</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select a voice" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getGoogleVoices().map((voice) => (
                  <SelectItem key={voice.id} value={voice.id}>
                    {voice.name} ({voice.gender}, {voice.accent})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select from available Google Cloud voices
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="speed"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Speed</FormLabel>
              <div className="text-foreground">
                {field.value || provider?.settings?.speed?.default || 1.0}x
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust the speed of speech generation.
            </FormDescription>
            <FormControl>
              <Slider
                min={provider?.settings?.speed?.min || 0.25}
                max={provider?.settings?.speed?.max || 4.0}
                step={0.1}
                defaultValue={[field.value || provider?.settings?.speed?.default || 1.0]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="pitch"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Pitch</FormLabel>
              <div className="text-foreground">
                {field.value || provider?.settings?.pitch?.default || 0}
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust the pitch of the voice.
            </FormDescription>
            <FormControl>
              <Slider
                min={provider?.settings?.pitch?.min || -20}
                max={provider?.settings?.pitch?.max || 20}
                step={1}
                defaultValue={[field.value || provider?.settings?.pitch?.default || 0]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="apiKey"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">Google Cloud API Key</FormLabel>
            <FormControl>
              <Input
                type="password"
                placeholder="Your Google Cloud API key"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormDescription className="text-muted-foreground">
              Required for Google Cloud TTS services
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

function AWSPollyFields({ control, setValue }) {
  const provider = ttsProviders.providers.aws;

  const getAWSModels = () => {
    return provider?.models || [];
  };

  const getAWSVoices = () => {
    return provider?.voices || [];
  };

  return (
    <>
      <FormField
        control={control}
        name="tts_model"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">TTS Model</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select TTS model" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getAWSModels().map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name}
                    {model.isDefault && " (Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              {getAWSModels().find(m => m.id === field.value)?.description || 
               "Amazon Polly TTS model"}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Voice</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select a voice" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getAWSVoices().map((voice) => (
                  <SelectItem key={voice.id} value={voice.id}>
                    {voice.name} ({voice.gender}, {voice.accent})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select from available Amazon Polly voices
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="speed"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Speed</FormLabel>
              <div className="text-foreground">
                {field.value || provider?.settings?.speed?.default || 1.0}x
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust the speed of speech generation.
            </FormDescription>
            <FormControl>
              <Slider
                min={provider?.settings?.speed?.min || 0.5}
                max={provider?.settings?.speed?.max || 2.0}
                step={0.1}
                defaultValue={[field.value || provider?.settings?.speed?.default || 1.0]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="volume"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Volume</FormLabel>
              <div className="text-foreground">
                {field.value || provider?.settings?.volume?.default || 1.0}
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust the volume level.
            </FormDescription>
            <FormControl>
              <Slider
                min={provider?.settings?.volume?.min || 0.1}
                max={provider?.settings?.volume?.max || 1.0}
                step={0.1}
                defaultValue={[field.value || provider?.settings?.volume?.default || 1.0]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="apiKey"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">AWS API Key</FormLabel>
            <FormControl>
              <Input
                type="password"
                placeholder="Your AWS API key"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormDescription className="text-muted-foreground">
              Required for Amazon Polly services
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

function AzureSpeechFields({ control, setValue }) {
  const provider = ttsProviders.providers.azure;

  const getAzureModels = () => {
    return provider?.models || [];
  };

  const getAzureVoices = () => {
    return provider?.voices || [];
  };

  return (
    <>
      <FormField
        control={control}
        name="tts_model"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">TTS Model</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select TTS model" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getAzureModels().map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name}
                    {model.isDefault && " (Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              {getAzureModels().find(m => m.id === field.value)?.description || 
               "Azure Speech Service model"}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Voice</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select a voice" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getAzureVoices().map((voice) => (
                  <SelectItem key={voice.id} value={voice.id}>
                    {voice.name} ({voice.gender}, {voice.accent})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select from available Azure Speech voices
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="speed"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Speed</FormLabel>
              <div className="text-foreground">
                {field.value || provider?.settings?.speed?.default || 1.0}x
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust the speed of speech generation.
            </FormDescription>
            <FormControl>
              <Slider
                min={provider?.settings?.speed?.min || 0.5}
                max={provider?.settings?.speed?.max || 2.0}
                step={0.1}
                defaultValue={[field.value || provider?.settings?.speed?.default || 1.0]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="pitch"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Pitch</FormLabel>
              <div className="text-foreground">
                {field.value || provider?.settings?.pitch?.default || 0}
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust the pitch of the voice.
            </FormDescription>
            <FormControl>
              <Slider
                min={provider?.settings?.pitch?.min || -50}
                max={provider?.settings?.pitch?.max || 50}
                step={1}
                defaultValue={[field.value || provider?.settings?.pitch?.default || 0]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="apiKey"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">Azure API Key</FormLabel>
            <FormControl>
              <Input
                type="password"
                placeholder="Your Azure Speech API key"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormDescription className="text-muted-foreground">
              Required for Azure Speech services
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

function PlayHTFields({ control, setValue }) {
  const provider = ttsProviders.providers.playai;

  const getPlayHTModels = () => {
    return provider?.models || [];
  };

  const getPlayHTVoices = () => {
    return provider?.voices || [];
  };

  return (
    <>
      <FormField
        control={control}
        name="tts_model"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">TTS Model</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select TTS model" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getPlayHTModels().map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name}
                    {model.isDefault && " (Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              {getPlayHTModels().find(m => m.id === field.value)?.description || 
               "PlayHT TTS model"}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormLabel className="text-foreground">Voice</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted text-foreground border-input">
                  <SelectValue placeholder="Select a voice" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getPlayHTVoices().map((voice) => (
                  <SelectItem key={voice.id} value={voice.id}>
                    {voice.name} ({voice.gender}, {voice.accent})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-muted-foreground">
              Select from available PlayHT voices
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="speed"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between mb-2">
              <FormLabel className="text-foreground">Speed</FormLabel>
              <div className="text-foreground">
                {field.value || provider?.settings?.speed?.default || 1.0}x
              </div>
            </div>
            <FormDescription className="text-muted-foreground mb-2">
              Adjust the speed of speech generation.
            </FormDescription>
            <FormControl>
              <Slider
                min={provider?.settings?.speed?.min || 0.1}
                max={provider?.settings?.speed?.max || 5.0}
                step={0.1}
                defaultValue={[field.value || provider?.settings?.speed?.default || 1.0]}
                onValueChange={(vals) => field.onChange(vals[0])}
                className="text-neon-purple"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="apiKey"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">PlayHT API Key</FormLabel>
            <FormControl>
              <Input
                type="password"
                placeholder="Your PlayHT API key"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormDescription className="text-muted-foreground">
              Required for PlayHT services
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

function GenericProviderFields({ control, setValue, providerId }) {
  const provider = ttsProviders.providers[providerId];

  const getProviderModels = () => {
    if (!provider?.models) return [];

    const allModels = [];
    if (Array.isArray(provider.models)) {
      allModels.push(...provider.models);
    } else {
      Object.entries(provider.models).forEach(([categoryName, modelCategory]) => {
        if (Array.isArray(modelCategory)) {
          allModels.push(...modelCategory);
        }
      });
    }

    return allModels;
  };

  const getProviderVoices = () => {
    return provider?.voices || [];
  };

  return (
    <>
      {getProviderModels().length > 0 && (
        <FormField
          control={control}
          name="tts_model"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel className="text-foreground">TTS Model</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-muted text-foreground border-input">
                    <SelectValue placeholder="Select TTS model" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {getProviderModels().map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                      {model.isDefault && " (Default)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription className="text-muted-foreground">
                {getProviderModels().find(m => m.id === field.value)?.description || 
                 `${provider?.name || providerId} TTS model`}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {getProviderVoices().length > 0 && (
        <FormField
          control={control}
          name="voice"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel className="text-foreground">Voice</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-muted text-foreground border-input">
                    <SelectValue placeholder="Select a voice" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {getProviderVoices().map((voice) => (
                    <SelectItem key={voice.id} value={voice.id}>
                      {voice.name} {voice.gender && voice.accent && `(${voice.gender}, ${voice.accent})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription className="text-muted-foreground">
                Select from available {provider?.name || providerId} voices
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {provider?.settings?.speed && (
        <FormField
          control={control}
          name="speed"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between mb-2">
                <FormLabel className="text-foreground">Speed</FormLabel>
                <div className="text-foreground">
                  {field.value || provider?.settings?.speed?.default || 1.0}x
                </div>
              </div>
              <FormDescription className="text-muted-foreground mb-2">
                Adjust the speed of speech generation.
              </FormDescription>
              <FormControl>
                <Slider
                  min={provider?.settings?.speed?.min || 0.5}
                  max={provider?.settings?.speed?.max || 2.0}
                  step={0.1}
                  defaultValue={[field.value || provider?.settings?.speed?.default || 1.0]}
                  onValueChange={(vals) => field.onChange(vals[0])}
                  className="text-neon-purple"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {provider?.requiresApiKey && (
        <FormField
          control={control}
          name="apiKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">{provider?.name || providerId} API Key</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder={`Your ${provider?.name || providerId} API key`}
                  className="bg-muted text-foreground border-input"
                  {...field}
                />
              </FormControl>
              <FormDescription className="text-muted-foreground">
                Required for {provider?.name || providerId} services
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </>
  );
}

function CustomProviderFields({ control }) {
  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name="voice_base_url"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">Base URL *</FormLabel>
            <FormControl>
              <Input
                placeholder="https://api.example.com"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice_model"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">Model *</FormLabel>
            <FormControl>
              <Input
                placeholder="custom-tts-model"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">Voice ID/Name *</FormLabel>
            <FormControl>
              <Input
                placeholder="custom-voice-id"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice_api_key"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">API Key</FormLabel>
            <FormControl>
              <Input
                type="password"
                placeholder="Your API key (optional)"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice_language"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">Language *</FormLabel>
            <FormControl>
              <Input
                placeholder="en"
                className="bg-muted text-foreground border-input"
                {...field}
              />
            </FormControl>
            <FormDescription className="text-muted-foreground">
              Language code (e.g., en, es, fr)
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function VoiceTuningSection({ control }) {
  const tts_provider = control._formValues.tts_provider;
  const provider = ttsProviders.providers[tts_provider];

  // Only show voice tuning for providers that support it
  if (!provider || !provider.settings) {
    return null;
  }

  return (
    <div className="mt-8 border rounded-md p-6 bg-background">
      <h3 className="text-lg font-medium text-foreground mb-2">
        Voice Tuning & Features
      </h3>
      <p className="text-muted-foreground mb-4">
        Fine-tune voice characteristics and enable additional features.
      </p>
      <div className="space-y-6">
        {provider.settings.stability && (
          <FormField
            control={control}
            name="stability"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between mb-2">
                  <FormLabel className="text-foreground">Stability</FormLabel>
                  <div className="text-foreground">
                    {field.value || provider.settings.stability.default}
                  </div>
                </div>
                <FormDescription className="text-muted-foreground mb-2">
                  Higher stability makes the voice more consistent but less
                  expressive.
                </FormDescription>
                <FormControl>
                  <Slider
                    min={provider.settings.stability.min}
                    max={provider.settings.stability.max}
                    step={0.1}
                    defaultValue={[
                      field.value || provider.settings.stability.default,
                    ]}
                    onValueChange={(vals) => field.onChange(vals[0])}
                    className="text-neon-purple"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {provider.settings.similarity_boost && (
          <FormField
            control={control}
            name="similarity_boost"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between mb-2">
                  <FormLabel className="text-foreground">
                    Similarity Boost
                  </FormLabel>
                  <div className="text-foreground">
                    {field.value || provider.settings.similarity_boost.default}
                  </div>
                </div>
                <FormDescription className="text-muted-foreground mb-2">
                  Boost the similarity of the synthesized speech to the original
                  voice.
                </FormDescription>
                <FormControl>
                  <Slider
                    min={provider.settings.similarity_boost.min}
                    max={provider.settings.similarity_boost.max}
                    step={0.1}
                    defaultValue={[
                      field.value || provider.settings.similarity_boost.default,
                    ]}
                    onValueChange={(vals) => field.onChange(vals[0])}
                    className="text-neon-purple"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {provider.settings.style && (
          <FormField
            control={control}
            name="style"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between mb-2">
                  <FormLabel className="text-foreground">Style</FormLabel>
                  <div className="text-foreground">
                    {field.value || provider.settings.style.default}
                  </div>
                </div>
                <FormDescription className="text-muted-foreground mb-2">
                  Amplify the style of the original speaker. Use higher values
                  for more expressive speech.
                </FormDescription>
                <FormControl>
                  <Slider
                    min={provider.settings.style.min}
                    max={provider.settings.style.max}
                    step={0.1}
                    defaultValue={[
                      field.value || provider.settings.style.default,
                    ]}
                    onValueChange={(vals) => field.onChange(vals[0])}
                    className="text-neon-purple"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {provider.settings.speed && (
          <FormField
            control={control}
            name="speed"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between mb-2">
                  <FormLabel className="text-foreground">Speed</FormLabel>
                  <div className="text-foreground">
                    {field.value || provider.settings.speed.default}x
                  </div>
                </div>
                <FormDescription className="text-muted-foreground mb-2">
                  Adjust the speed of speech generation. Higher values make
                  speech faster.
                </FormDescription>
                <FormControl>
                  <Slider
                    min={provider.settings.speed.min}
                    max={provider.settings.speed.max}
                    step={0.1}
                    defaultValue={[
                      field.value || provider.settings.speed.default,
                    ]}
                    onValueChange={(vals) => field.onChange(vals[0])}
                    className="text-neon-purple"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {provider.settings.useSpeakerBoost && (
          <FormField
            control={control}
            name="useSpeakerBoost"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base text-foreground">
                    Use Speaker Boost
                  </FormLabel>
                  <FormDescription className="text-muted-foreground">
                    Boost the similarity of the synthesized speech and the voice
                    at the cost of some generation speed.
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={
                      field.value || provider.settings.useSpeakerBoost.default
                    }
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );
}

export default function VoiceTab() {
  const { control, setValue, watch } = useFormContext();
  const [isLoading, setIsLoading] = useState(false);
  const tts_provider = watch("tts_provider");
  const llm_provider = watch("llm_provider");
  const llm_model = watch("llm_model");

  // Check if using a realtime model
  const isUsingRealtimeModel = isRealtimeModel(llm_provider, llm_model);
  const isOpenAIRealtime = llm_provider === "openai-realtime";
  const isCustomRealtime = llm_provider === "custom-realtime";
  const usesDedicatedRealtimeVoiceField = isOpenAIRealtime || isCustomRealtime;
  // Gemini Live speaks with its own prebuilt voices, configured on the Model tab —
  // the TTS voice picked here is ignored unless it happens to match a Gemini voice.
  const isUsingGeminiRealtime = isGeminiRealtimeModel(llm_provider, llm_model);

  useEffect(() => {
    // Set defaults if no provider selected
    if (!watch("tts_provider")) {
      const defaultProvider = Object.values(ttsProviders.providers)[0];
      setValue("tts_provider", defaultProvider.id, { shouldValidate: true });

      // Set default model if available
      if (defaultProvider.models) {
        let allModels = [];
        
        // Handle different model structure formats
        if (Array.isArray(defaultProvider.models)) {
          // Direct array (like Sarvam)
          allModels = defaultProvider.models;
        } else {
          // Categorized models (like ElevenLabs)
          Object.entries(defaultProvider.models).forEach(
            ([categoryName, modelCategory]) => {
              if (Array.isArray(modelCategory)) {
                allModels.push(...modelCategory);
              }
            }
          );
        }
        
        const defaultModel = allModels.find((m) => m.isDefault) || allModels[0];
        if (defaultModel) {
          setValue("tts_model", defaultModel.id, { shouldValidate: true });
        }
      }
    }
  }, [setValue, watch]);

  return (
    <div className="space-y-6">
      {/* Show alert if a realtime model is selected */}
      {isUsingRealtimeModel && (
        <Alert className="bg-secondary border-border">
          <InfoIcon className="h-4 w-4 text-foreground" />
          <AlertDescription className="text-sm text-foreground">
            {isOpenAIRealtime
              ? `You're using OpenAI Realtime (${llm_model}). Pick one of its built-in voices below — the TTS provider settings don't apply to this model.`
              : isCustomRealtime
              ? `You're using a custom realtime endpoint. Type the voice name your endpoint expects below — the TTS provider settings don't apply to this model.`
              : isUsingGeminiRealtime
              ? `You're using Gemini Live (${llm_model}), which speaks with its own prebuilt voices. The settings below are ignored — set the voice and language with the "Realtime Voice" and "Realtime Language" fields on the Model tab.`
              : `You're using a realtime model (${llm_model}). Voice settings below will be used to configure the model's built-in text-to-speech capabilities.`}
          </AlertDescription>
        </Alert>
      )}

      <div
        className="space-y-6"
        style={{
          opacity: isUsingGeminiRealtime ? 0.5 : 1,
          pointerEvents: isUsingGeminiRealtime ? "none" : "auto",
        }}
      >
        <div className="border rounded-md p-6 bg-background">
          <h3 className="text-lg font-medium text-foreground mb-2">
            Voice Selection
          </h3>
          <p className="text-muted-foreground mb-4">
            Choose the provider, TTS model, and specific voice for your
            assistant.
          </p>
          <div className="space-y-6">
            {isOpenAIRealtime ? (
              <FormField
                control={control}
                name="voice"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-foreground">Realtime Voice</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="bg-muted text-foreground border-input">
                          <SelectValue placeholder="Select a voice" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {OPENAI_REALTIME_VOICES.map((voiceOption) => (
                          <SelectItem key={voiceOption.id} value={voiceOption.id}>
                            {voiceOption.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Built-in voice used by the OpenAI Realtime model.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : isCustomRealtime ? (
              <FormField
                control={control}
                name="voice"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-foreground">Realtime Voice</FormLabel>
                    <FormControl>
                      <Input
                        className="bg-muted text-foreground border-input"
                        placeholder="e.g. alloy, verse, or your endpoint's voice name"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormDescription>
                      Voice name passed to your custom realtime endpoint.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
            <>
            <FormField
              control={control}
              name="tts_provider"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground">Provider</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);

                      // Set default model and voice for the new provider
                      const provider = ttsProviders.providers[value];
                      if (provider && provider.models) {
                        let allModels = [];
                        
                        // Handle different model structure formats
                        if (Array.isArray(provider.models)) {
                          // Direct array (like Sarvam)
                          allModels = provider.models;
                        } else {
                          // Categorized models (like ElevenLabs)
                          Object.entries(provider.models).forEach(
                            ([categoryName, modelCategory]) => {
                              if (Array.isArray(modelCategory)) {
                                allModels.push(...modelCategory);
                              }
                            }
                          );
                        }
                        
                        const defaultModel =
                          allModels.find((m) => m.isDefault) || allModels[0];
                        if (defaultModel) {
                          setValue("tts_model", defaultModel.id, {
                            shouldValidate: true,
                          });
                        }
                      }
                    }}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="bg-muted text-foreground border-input">
                        <SelectValue placeholder="Select a provider" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(ttsProviders.providers).map((provider) => (
                        <SelectItem key={provider.id} value={provider.id}>
                          {provider.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {tts_provider === "elevenlabs" && (
              <ElevenLabsFields control={control} setValue={setValue} />
            )}

            {tts_provider === "smallestai" && (
              <SmallestAIFields control={control} setValue={setValue} />
            )}

            {tts_provider === "groq" && (
              <GroqTTSFields control={control} setValue={setValue} />
            )}

            {tts_provider === "cartesia" && (
              <CartesiaFields control={control} setValue={setValue} />
            )}

            {tts_provider === "sarvam" && (
              <SarvamTTSFields control={control} setValue={setValue} />
            )}

            {tts_provider === "deepgram" && (
              <DeepgramTTSFields control={control} setValue={setValue} />
            )}

            {tts_provider === "resemble" && (
              <ResembleFields control={control} setValue={setValue} />
            )}

            {tts_provider === "openai" && (
              <OpenAIFields control={control} setValue={setValue} />
            )}

            {tts_provider === "google" && (
              <GoogleTTSFields control={control} setValue={setValue} />
            )}

            {tts_provider === "aws" && (
              <AWSPollyFields control={control} setValue={setValue} />
            )}

            {tts_provider === "azure" && (
              <AzureSpeechFields control={control} setValue={setValue} />
            )}

            {tts_provider === "playai" && (
              <PlayHTFields control={control} setValue={setValue} />
            )}

            {/* Generic handlers for remaining providers */}
            {tts_provider === "mistralai" && (
              <GenericProviderFields control={control} setValue={setValue} providerId="mistralai" />
            )}

            {tts_provider === "baseten" && (
              <GenericProviderFields control={control} setValue={setValue} providerId="baseten" />
            )}

            {tts_provider === "hume" && (
              <GenericProviderFields control={control} setValue={setValue} providerId="hume" />
            )}

            {tts_provider === "inworld" && (
              <GenericProviderFields control={control} setValue={setValue} providerId="inworld" />
            )}

            {tts_provider === "lmnt" && (
              <GenericProviderFields control={control} setValue={setValue} providerId="lmnt" />
            )}

            {tts_provider === "neuphonic" && (
              <GenericProviderFields control={control} setValue={setValue} providerId="neuphonic" />
            )}

            {tts_provider === "rime" && (
              <GenericProviderFields control={control} setValue={setValue} providerId="rime" />
            )}

            {tts_provider === "speechify" && (
              <GenericProviderFields control={control} setValue={setValue} providerId="speechify" />
            )}

            {tts_provider === "spitch" && (
              <GenericProviderFields control={control} setValue={setValue} providerId="spitch" />
            )}

            {tts_provider === "murf" && (
              <MurfFields control={control} setValue={setValue} />
            )}

            {tts_provider === "voxi-tts" && (
              <GenericProviderFields control={control} setValue={setValue} providerId="voxi-tts" />
            )}

            {tts_provider === "custom" && (
              <CustomProviderFields control={control} />
            )}
            </>
            )}
          </div>
        </div>
      </div>

      {!usesDedicatedRealtimeVoiceField && <VoiceTuningSection control={control} />}
    </div>
  );
}
